import {DNA} from "@dna/react";
import {Box, Grid, makeStyles} from "@material-ui/core";
import React from "react";
import {SelectableMedication} from "../usePharmacyMedications";
import {MedicationCourseIcon} from "@communicare/medications/Components/MedicationCourseIcon";
import {MedicationCourse, MedicationExpiry, MedicationStatus} from "@communicare/medications/Contracts/MedicationModels";
import {CourseType, MedicationState, ProductStatus} from "../../../services/medicationmanagement/medications";
import {MedicationCardMode} from "@communicare/medications/Components/MedicationCard";
import {ArrowDropRightIcon, MedicationFinalisedIcon, MedicationStoppedIcon, ClockIcon} from "@communicare/medications/Components/Icons";
import {Button, ButtonMode, ButtonSize, ButtonVariant} from "@communicare/medications/Components/Button";
import {iconLight} from "@communicare/medications/Styles/Colours";
import {SelectableMedicationStatusBadge} from "../SelectableMedicationStatusBadge";
import {MedicationStateTextStyle} from "../MedicationStateTextStyle";
import {RenderIf} from "../../common/render-if/RenderIf";
import {ReactComponent as DoseTimeEditor} from "../../../images/doseTimeEditor.svg";
import styles from './PharmacyMedicationCard.module.scss';
import { PharmacyMedicationProductDisplay } from "./PharmacyMedicationProductDisplay";

let useStyles = makeStyles({
    container:{
      flexWrap:"nowrap",
        "&.condensed":{
          padding:"16px 11px 16px 0"
        }
    },
    productName:{
        fontWeight: DNA.tokens.font.weight.semiBold,
        //wordWrap:"break-word",
        overflow:"hidden",
        "& .standard":{
            fontSize:16    
        },
        "& .condensed":{
            fontSize:14
        }
    },
    productDosage:{
        //fontSize:14,
        color:DNA.tokens.color.gray["600"],
        "&.standard":{
            fontSize:14
        },
        "&.condensed":{
            fontSize:12
        }
    },
    icons: {
        position: "relative",
        marginRight: "12px",
        alignItems: "center"
    },
    statusIcon:{
        position: "absolute", 
            bottom: "1px",
            right: "0",

        "&.nudge": {
            bottom: "-4px",
            right: "-3px"
        }
    },
    arrowButton:{
        display:"flex",
        justifyContent:"center"
    }
});


const CourseTypeMap = new Map<CourseType,MedicationCourse>();
CourseTypeMap.set(CourseType.AsRequired, MedicationCourse.AsRequired);
CourseTypeMap.set(CourseType.ShortCourse, MedicationCourse.ShortCourse);
CourseTypeMap.set(CourseType.Regular, MedicationCourse.Regular);


export const StandardPharmacyMedicationCard =(props:{
    item:SelectableMedication;
})=>{
    return <PharmacyMedicationCard mode={"Standard"} item={props.item} isSelected={false}  onActioned={()=>{}} onDosageClicked={()=>{}}/>
}

export const CollapsedPharmacyMedicationCard =(props:{
    item:SelectableMedication;
    isSelected:boolean;
    onActioned:(authoringSystem:string,authoringSystemForeignKey:string)=>void;
    onDosageClicked:(authoringSystem:string,authoringSystemForeignKey:string)=>void;
})=>{
    return <PharmacyMedicationCard mode={"Condensed"} {...props}/>
}

export const ActiveMedicationCourseIcon = (props:{
    item:SelectableMedication;
})=>{ 
       let classes= useStyles();
       return <Box className={classes.icons}>
	            <MedicationCourseIcon 
	                course={CourseTypeMap.get(props.item.courseType)||MedicationCourse.Regular} 
	                expiry={props.item.medicationExpiryStatus} 
	                status={props.item.status===MedicationState.Existing?MedicationStatus.Finalised :MedicationStatus.Draft}
	                mode={MedicationCardMode.Standard}
	            />

        </Box>
}  

export const StoppedMedicationCourseIcon = (props:{
    item:SelectableMedication;
})=>{
    let classes= useStyles();
    
    return  <Box className={classes.icons}>
                <MedicationCourseIcon 
                    course={CourseTypeMap.get(props.item.courseType)||MedicationCourse.Regular} 
                    expiry={props.item.medicationExpiryStatus} 
                    status={MedicationStatus.Stopped }
                    mode={MedicationCardMode.Standard}></MedicationCourseIcon>
                
           </Box>
}

const PharmacyMedicationCard = (props:{
    isSelected:boolean;
    item:SelectableMedication;
    mode?:"Standard"|"Condensed";
    onActioned:(authoringSystem:string,authoringSystemForeignKey:string)=>void;
    onDosageClicked:(authoringSystem:string,authoringSystemForeignKey:string)=>void;
})=>{
    let classes= useStyles();
    let mode = props.mode??"Standard";

    return (
        <>
        {mode === "Standard"?
        <Grid container spacing={1} className={classes.container}>

        <Grid item>
        {!(props.item.status === MedicationState.Ceased && props.item.confirmed) ?
            <ActiveMedicationCourseIcon item = {props.item} />
                : <StoppedMedicationCourseIcon item = {props.item} />
        }
        </Grid>
        <Grid item>
            <Grid container direction="column" className={styles.medicationNameContainer}>
                <Grid item>
                    <Box className={`${classes.productName} standard`}>
                        <MedicationStateTextStyle 
                            medicationState={props.item.status} 
                            stateConfirmed= {props.item.confirmed}
                        >
                            {props.item.product.name}    
                        </MedicationStateTextStyle>
                    </Box>
                </Grid>
            </Grid>
        </Grid>
    </Grid>:
            <Grid container spacing={0} className={`${classes.container} condensed`}>
                <Grid item xs={1} className={classes.arrowButton}>
                    <Button
                        onClick={() => {
                            
                        }}
                        iconOnly
                        size={ButtonSize.small}
                        mode={ButtonMode.text}
                        noShadowOnHover
                        testId='expand-group'
                        subtleShadeOnHover
                        variant={ButtonVariant.primary}
                    >
                        
                            <ArrowDropRightIcon color={iconLight} />
                        
                    </Button>
                </Grid>
                <Grid item>
                    {!(props.item.status === MedicationState.Ceased && props.item.confirmed) ?
                        <ActiveMedicationCourseIcon item = {props.item} />
                            : <StoppedMedicationCourseIcon item = {props.item} />
                    }
                </Grid>
                <Grid item xs={9}>
                    <Grid container direction="column" spacing={0}>
                        <Grid item>
                            <Box className={`${classes.productName} condensed`}>
                                <MedicationStateTextStyle 
                                    medicationState={props.item.status}  
                                    stateConfirmed= {props.item.confirmed}
                                >
                                {props.item.product.name}
                                </MedicationStateTextStyle>
                            </Box>
                        </Grid>
                        <Grid item>
                            <Box className={`${classes.productDosage} condensed`}>
                                <MedicationStateTextStyle 
                                    medicationState={props.item.status} 
                                    stateConfirmed= {props.item.confirmed}
                                 >
                                        <PharmacyMedicationProductDisplay item={props.item} />
                                </MedicationStateTextStyle>
                            </Box>
                        </Grid>
                        <Grid item>
                                <RenderIf if={props.item.confirmed != true}>
                                    <SelectableMedicationStatusBadge
                                        medicationState={props.item.status}
                                        checked={props.item.product?.action == ProductStatus.Actioned}
                                        onChange={() => {
                                            if (props.onActioned) {
                                                props.onActioned(props.item.authoringSystem, props.item.authoringSystemForeignKey)
                                            }
                                        }}
                                    />
                                </RenderIf>
                            
                        </Grid>
                    </Grid>

                </Grid>
                <Grid item>
                    <RenderIf if={props.isSelected && props.item.status!==MedicationState.Ceased}>
                        <Grid container direction={"column-reverse"} spacing={0} className={styles.dosageContainer}>

                            <Grid item>
                                <DoseTimeEditor onClick={_=>props.onDosageClicked(props.item.authoringSystem, props.item.authoringSystemForeignKey)}/>
                            </Grid>
                            <Grid item xs={8}>
                                <></>
                            </Grid>
                        </Grid>    
                    </RenderIf>
                </Grid>
            </Grid>
        }
        </>
    );
}